import { getSmartResizeFormatsForSizeSet } from '@air/feature-edit-canvas';
import { type SavedSize, type SizeSet } from '@air/feature-saved-sizes/utils';

import { type EditInCanvasSizeSet } from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';

export type PickMostRecentSizeSetParams = {
  sizePresets: SavedSize[];
  sizeSets: SizeSet[];
};

const getUpdatedAtTime = (sizeSet: SizeSet) => {
  const time = Date.parse(sizeSet.updatedAt);

  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

/**
 * The API promises no order for sets, so the pick is the most recently updated set (ties broken
 * by name) that resolves to at least one image size. A set whose members were all deleted, or are
 * all sizes images can't take, is skipped rather than offered as "Resize for X · 0 sizes".
 */
export const pickMostRecentSizeSet = ({
  sizePresets,
  sizeSets,
}: PickMostRecentSizeSetParams): EditInCanvasSizeSet | null => {
  const newestFirst = [...sizeSets].sort((a, b) => {
    const aTime = getUpdatedAtTime(a);
    const bTime = getUpdatedAtTime(b);

    if (aTime !== bTime) {
      return bTime > aTime ? 1 : -1;
    }

    return a.name.localeCompare(b.name);
  });

  for (const sizeSet of newestFirst) {
    const formats = getSmartResizeFormatsForSizeSet(sizeSet, sizePresets);

    if (formats.length > 0) {
      return { formats, id: sizeSet.id, name: sizeSet.name };
    }
  }

  return null;
};
